import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { inject } from '@vercel/analytics';

import { supabase } from './services/supabaseClient/supabaseClient';
import { initializeAuth } from './redux/authSlice';
import AppRoutes from './routes/AppRoutes';
import { OrchestratorAgentProvider } from './context/OrchestratorAgentContext';
import { AgentModalProvider } from './context/AgentModalContext';
import installLlmEventInterceptor from './services/llmClient/llmEventInterceptor';

// Import transition system
import './styles/transitions.css';

function enforceSingleWordButtonLabels(root = document) {
  const buttons = root instanceof HTMLButtonElement ? [root] : root.querySelectorAll('button');

  const shortenLabel = (button) => {
    if (!button || button.dataset.keepFullLabel === 'true') return;

    const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let text = '';
    let current = walker.nextNode();
    while (current) {
      const value = String(current.textContent || '').trim();
      if (value) {
        textNodes.push(current);
        text += `${value} `;
      }
      current = walker.nextNode();
    }

    const fullLabel = text.trim();
    if (!fullLabel) return;
    const words = fullLabel.split(/\s+/);
    if (words.length <= 1) return;

    const firstWord = words[0];
    button.setAttribute('title', fullLabel);
    button.setAttribute('aria-label', fullLabel);

    if (textNodes.length === 0) return;

    let wrote = false;
    for (const node of textNodes) {
      if (!wrote) {
        node.textContent = firstWord;
        wrote = true;
      } else {
        node.textContent = '';
      }
    }
  };

  Array.from(buttons).forEach(shortenLabel);
}

const App = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.id || null);
  inject();
  installLlmEventInterceptor();

  useEffect(() => {
    // 🟡 App mounted — kick off initialization

    dispatch(initializeAuth());

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      dispatch(initializeAuth());

      // Redirect to courses page after email verification
      if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
        const hash = window.location.hash;
        // Check if this is from an email verification link
        if (hash.includes('type=signup') || hash.includes('type=email')) {
          window.location.href = '/courses';
        }
      }
    });

    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    enforceSingleWordButtonLabels(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            if (node.tagName === 'BUTTON') {
              enforceSingleWordButtonLabels(node);
            } else if (node.querySelectorAll) {
              enforceSingleWordButtonLabels(node);
            }
          });
        } else if (mutation.type === 'characterData') {
          const parent = mutation.target?.parentElement;
          if (parent && parent.closest) {
            const button = parent.closest('button');
            if (button) enforceSingleWordButtonLabels(button.parentElement || document);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  const navigate = (view, payload) => {
    // TODO: integrate with your router or global view state
    // e.g., dispatch(setCurrentView(view));
    // For now this is a no-op placeholder.
    // eslint-disable-next-line no-console
    console.log('[Orchestrator navigate]', view, payload);
  };

  const focusTask = (taskId) => {
    // TODO: integrate with your task selection UX
    // e.g., dispatch(selectTask(taskId));
    // For now this is a no-op placeholder.
    // eslint-disable-next-line no-console
    console.log('[Orchestrator focusTask]', taskId);
  };

  const getAppContext = () => ({
    // Provide minimal app context for now; expand as needed
    currentPage: window.location.pathname.replace('/', '') || 'root',
    selectedTaskId: null,
  });

  return (
    <OrchestratorAgentProvider
      userId={userId}
      navigate={navigate}
      focusTask={focusTask}
      getAppContext={getAppContext}
    >
      <AgentModalProvider>
        <AppRoutes />
      </AgentModalProvider>
    </OrchestratorAgentProvider>
  );
};

export default App;
